/**
 * Forgot password — Untitled-UI-style two-step flow.
 *
 *   Step 1 (form):    "Forgot password?" / email input / Reset password
 *   Step 2 (success): Mail featured icon / "Check your email" /
 *                     "We sent a password reset link to <email>" /
 *                     Resend + Back to log in
 *
 * Endpoint: POST /v1/auth/forgot-password (mocked — see mock-handlers.ts).
 * Always returns OK regardless of email — by design we don't reveal
 * whether the address exists.
 */
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import { ArrowLeft, Mail01 } from "@untitledui/icons";
import { BrandLogo, Button, FeaturedIcon, Input } from "@tricura/atlas-ui";
import { requestPasswordReset } from "./api";

export function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!email.trim()) {
            setError("Enter your email to continue.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await requestPasswordReset(email);
            setSent(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not send the reset link. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    /** Send the user back to the form so they can fix a typo, switch
     *  email, or just hit Reset password again. We keep the email
     *  pre-filled — most resends are "I didn't get it, try again." */
    const reopenForm = () => {
        setSent(false);
        setError(null);
    };

    return (
        <section className="bg-primary sm:bg-secondary min-h-screen px-4 py-12 md:px-8 md:pt-24">
            <div className="bg-primary sm:max-w-110 flex w-full flex-col gap-6 sm:mx-auto sm:rounded-2xl sm:px-10 sm:py-8 sm:shadow-sm">
                {!sent ? (
                    <>
                        <div className="flex flex-col items-center gap-6 text-center">
                            <BrandLogo />
                            <div className="flex flex-col gap-2">
                                <h1 className="text-display-xs text-primary font-semibold">Forgot password?</h1>
                                <p className="text-md text-tertiary">No worries, we'll send you reset instructions.</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                            <Input
                                isRequired
                                type="email"
                                name="email"
                                size="md"
                                placeholder="Enter your email"
                                autoComplete="email"
                                value={email}
                                onChange={setEmail}
                                data-autotest="forgot-password-email-input"
                            />

                            {error && (
                                <p role="alert" className="text-error-primary text-sm" data-autotest="forgot-password-error">
                                    {error}
                                </p>
                            )}

                            <Button type="submit" size="lg" isLoading={submitting} showTextWhileLoading data-autotest="forgot-password-button">
                                Reset password
                            </Button>
                        </form>

                        <BackToLogin />
                    </>
                ) : (
                    <>
                        <div className="flex flex-col items-center gap-6 text-center">
                            <FeaturedIcon icon={Mail01} color="brand" theme="light" size="xl" />
                            <div className="flex flex-col gap-2">
                                <h1 className="text-display-xs text-primary font-semibold">Check your email</h1>
                                <p className="text-md text-tertiary">
                                    We sent a password reset link to <span className="text-primary font-medium">{email}</span>
                                </p>
                            </div>
                        </div>

                        {error && (
                            <p role="alert" className="text-error-primary text-center text-sm">
                                {error}
                            </p>
                        )}

                        <Button size="lg" onClick={() => navigate("/login")} data-autotest="forgot-password-back-login-button" >
                            Back to log in
                        </Button>

                        <p className="text-tertiary text-center text-sm">
                            Didn't receive the email?{" "}
                            <button type="button" onClick={reopenForm} className="text-brand-secondary cursor-pointer font-semibold" data-autotest="forgot-password-resend-button">
                                Click to resend
                            </button>
                        </p>
                    </>
                )}
            </div>
        </section>
    );
}

function BackToLogin() {
    return (
        <RouterLink
            data-autotest="forgot-password-back-login-link"
            to="/login"
            className="text-tertiary hover:text-secondary inline-flex items-center justify-center gap-2 text-sm font-semibold transition duration-100 ease-linear"
        >
            <ArrowLeft className="size-4" />
            Back to log in
        </RouterLink>
    );
}
